import { ROADS_AND_SEWERS_TAKEOFF_COLUMN_MAP } from "./roadsAndSewersTakeoffColumnMap";

export interface TakeoffValidationResult {
  isValid: boolean;
  errors: string[];
  /** True when none of the expected worksheets were found at all — the
   * uploaded file isn't a Roads & Sewers take-off workbook (wrong file
   * entirely), rather than a real take-off file with a few sheets/columns
   * out of sync with the template. Callers should show a plain "wrong file"
   * message instead of the itemized `errors` list in this case. */
  isWrongFileType: boolean;
}

/**
 * Validates an uploaded Roads & Sewers take-off workbook against
 * `ROADS_AND_SEWERS_TAKEOFF_COLUMN_MAP` (derived from `public/R&S Take-off
 * sheet.xlsx`). Same approach as `groundworksTakeoffValidation.ts`: for
 * every mapped sheet, every column's `key` must be found at its exact
 * `cellRef` (a direct cell-address lookup) — not merely present anywhere in
 * the sheet. This works identically for both layouts this map contains
 * (column-per-field sheets with the key in row 1, and row-per-item sheets
 * with the key down a single column), since `cellRef` is just an address
 * either way. A sheet missing entirely, or having even one mapped key not at
 * its exact expected cell, fails the whole file.
 */
export async function validateRoadsAndSewersTakeoff(
  file: File,
): Promise<TakeoffValidationResult> {
  const XLSX = await import("xlsx");
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });

  const errors: string[] = [];
  const sheetEntries = Object.entries(ROADS_AND_SEWERS_TAKEOFF_COLUMN_MAP);
  let missingSheetCount = 0;

  for (const [sheetName, columns] of sheetEntries) {
    const worksheet = workbook.Sheets[sheetName];
    if (!worksheet) {
      errors.push(`Missing worksheet: "${sheetName}"`);
      missingSheetCount++;
      continue;
    }

    let mismatchedColumn: (typeof columns)[number] | null = null;
    for (const column of columns) {
      const cell = worksheet[column.cellRef];
      const value = cell !== undefined ? String(cell.v).trim() : "";
      if (value !== column.key) {
        mismatchedColumn = column;
        break;
      }
    }
    if (mismatchedColumn) {
      const columnDescriptor = mismatchedColumn.label
        ? `"${mismatchedColumn.label}" (cell ${mismatchedColumn.cellRef})`
        : `at cell ${mismatchedColumn.cellRef}`;
      errors.push(
        `Worksheet "${sheetName}" has a column that doesn't match the take-off template — mismatch at column ${columnDescriptor}`,
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    isWrongFileType: missingSheetCount === sheetEntries.length,
  };
}
